import type { FunctionComponent } from 'react';
import { InfoIcon } from 'lucide-react';

import type { EngagementType } from '../types';
import { ctw } from '@/common';
import { Card, CardContent, CardHeader } from '@/components';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms';
import { engagementMetricsMapper } from '../constants';

export const EngagementMetrics: FunctionComponent<{
  engagements: EngagementType[];
}> = ({ engagements }) => (
  <Card className="h-full w-1/2 2xl:!h-1/2 2xl:!w-full">
    <CardHeader className="px-6 pb-2 pt-4 font-bold">Engagement</CardHeader>

    <CardContent className="mt-4 flex h-3/5 flex-col justify-around gap-4 px-4 py-2 text-sm">
      {engagements.length > 0 ? (
        engagements.map(({ label, value }) => {
          const { suffix, description } = engagementMetricsMapper[label];

          return (
            <div
              key={label}
              className="grid w-full grid-cols-5 items-center justify-between 2xl:ml-2 2xl:grid-cols-2"
            >
              <div className="col-span-3 flex flex-nowrap items-center gap-2 2xl:col-span-1">
                <p className="whitespace-nowrap text-gray-500">{label}</p>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="cursor-help">
                      <InfoIcon className="h-4 w-4 text-gray-500" />
                    </TooltipTrigger>

                    <TooltipContent
                      side="right"
                      align="center"
                      className="text-primary max-w-[12rem] border border-gray-400 bg-gray-50 text-sm"
                    >
                      <p className="text-sm text-gray-500">{description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="col-span-2 2xl:col-span-1">
                <span className="text-sm font-bold">{value}</span>
                <span className={ctw('text-sm', suffix === '%' && 'font-bold')}>{suffix}</span>
              </div>
            </div>
          );
        })
      ) : (
        <div className="col-span-1 flex h-full items-center justify-center sm:col-span-2 md:col-span-3">
          <p>No Engagement Data Available</p>
        </div>
      )}
    </CardContent>
  </Card>
);
